import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './MediaModal.module.css';

interface MediaItem {
  id: string;
  src: string;
  type: 'image' | 'video';
  alt?: string;
}

interface MediaModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaItem: MediaItem | null;
}

const MediaModal: React.FC<MediaModalProps> = ({ isOpen, onClose, mediaItem }) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Previene lo scroll del body
      document.body.style.overflowX = 'hidden'; // Previene lo scroll orizzontale
      document.body.classList.add('modal-open'); // Aggiungi classe per cursore

      // Nota: non blocchiamo gli eventi touch globali per evitare di impedire i click sul backdrop/close
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
        document.body.style.overflowX = 'unset';
        document.body.classList.remove('modal-open'); // Rimuovi classe
      };
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
      document.body.style.overflowX = 'unset';
      document.body.classList.remove('modal-open'); // Rimuovi classe
    };
  }, [isOpen, onClose]);

  if (!isOpen || !mediaItem) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Chiudi solo se clic fuori dal contenuto
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div className={styles.modalBackdrop} onClick={handleBackdropClick} role="dialog" aria-modal="true">
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose}>
          ×
        </button>
        
        <div className={styles.mediaContainer}>
          {mediaItem.type === 'image' ? (
            <img
              src={mediaItem.src}
              alt={mediaItem.alt || 'Immagine ingrandita'}
              className={styles.mediaImage}
            />
          ) : (
            <video
              src={mediaItem.src}
              className={styles.mediaVideo}
              controls
              autoPlay
              loop
              playsInline
            >
              <source src={mediaItem.src} type="video/mp4" />
              Il tuo browser non supporta il tag video.
            </video>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MediaModal;
